import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, ViewChildren, ChangeDetectionStrategy} from '@angular/core';
import {Field} from "../../interfaces/field";
import {AbstractControl, FormArray, FormGroup, UntypedFormArray, UntypedFormControl} from "@angular/forms";
import {StateService} from "../../services/state.service";
import {FieldErrorService} from "../../services/field-error.service";
import {AddressFieldComponent} from "../address-field/address-field.component";

@Component({
    selector: 'app-sortable-address-field',
    templateUrl: './sortable-address-field.component.html',
    styleUrls: ['./sortable-address-field.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SortableAddressFieldComponent implements OnChanges, OnInit, OnDestroy{
  @Input() departureConfig: Field;
  @Input() destinationConfig: Field;
  @Input() form: FormGroup;
  @Input() allowStopOversRoute = false;
  @Input() allowStopOversHourly = false;
  @Input() bookingHourlyDestination!: any;

  /**
   * Stopovers are configured per booking type, so the "add stopover" control follows the
   * active tab. This is resolved from the two flags here instead of being passed in as one
   * pre-computed input: Angular assigns inputs in template order, so the `activeTab` setter
   * below would still see the previous tab's value of such an input.
   */
  get allowStopOvers(): boolean {
    return this._activeTab === 'hourly' ? this.allowStopOversHourly : this.allowStopOversRoute;
  }

  private _activeTab!: string;
  @Input()
  set activeTab(value: string) {
    this._activeTab = value;

    if(this.form && this._state) {
      if (!this.bookingHourlyDestination) {
        if (this._activeTab == 'route') {
          // Only add the destination if it isn't already the last entry. Otherwise
          // (e.g. when the tab is set to 'route' after the component already built its
          // addressArray via ngOnInit) we'd push a second destination, which renders as
          // a phantom stopover row regardless of allowStopOvers.
          const last = this.addressArray[this.addressArray.length - 1];
          if (!last || last.control !== this.form.controls['destination']) {
            this.addressArray.push({
              control:this.form.controls['destination'],
              controlModel:this.form.controls['destinationModel'],
              config: this.destinationConfig
            });
          }
        } else {
          // Stopover rows are NOT dropped here: whether they survive the switch is decided by
          // the booking type's own stopover setting, applied once below. (This used to splice
          // them away with a forEach over the array it was mutating, which skipped every other
          // row — harmless while hourly could never keep stopovers, silently destructive now
          // that it can.)
          //
          // Only remove the trailing entry if it is actually the destination, so a
          // repeated/out-of-order hourly switch can't strip a real address row.
          const last = this.addressArray[this.addressArray.length - 1];
          if (last && last.control === this.form.controls['destination']) {
            this.addressArray.splice((this.addressArray.length - 1), 1);
          }

          this.form.controls['stopOversModel'].updateValueAndValidity();
          this.form.controls['destination'].setValue(null);
          this.form.controls['destinationModel'].setValue(null);
        }
      }

      // Stopovers can be enabled for one booking type and disabled for the other, so a row
      // added on the previous tab must not survive the switch — it would still render (any
      // middle row is a stopover by position) and still reach the payload.
      if (!this.allowStopOvers) {
        this.removeAllStopOvers();
      }
    }
  }

  get activeTab(): string {
    return this._activeTab;
  }

  _state: StateService
  @Output() openHelpModal: EventEmitter<any> = new EventEmitter<any>();
  @Output() checkWorkArea: EventEmitter<any> = new EventEmitter<any>();
  @ViewChildren(AddressFieldComponent) addressFields: AddressFieldComponent[];
  protected addressArray = [];
  protected stopOverConfig;
  protected drag: any;
  protected yPos: number;
  private xPos: number;
  protected yOrigin: number;
  private xOrigin: number;
  private change: number;
  private rowHeight = 75;

  constructor(private state: StateService,
              public errors: FieldErrorService) {
    this._state = state;
  }

  convertToFormControl(absCtrl: AbstractControl | null): UntypedFormArray {
    const ctrl = absCtrl as FormArray;
    return ctrl;
  }

  /**
   * Reconciles the rows with a stopover setting that was switched off after this component was
   * built. applyFreePlanOverrides() does exactly that: the contract check behind it is fired in
   * the background by FormResolverService and does not block the form, so it lands once the form
   * has already rendered. Waiting for a tab change would not do — on a single-booking-type form
   * that change never comes, and the rows would then be drawn against controls that were cleared
   * from OrderComponent, which makes checkFieldValidity() dereference a missing control.
   *
   * Deliberately restricted to "off for BOTH booking types", the only way these flags change at
   * runtime. That verdict does not depend on the tab, so it is safe however late the tab settles;
   * a plain `!allowStopOvers` check here would run against the tab this component happened to be
   * built with and throw away the stopovers a deferred `preOrder` restore was about to bring back.
   * The per-tab case is handled once the tab has settled, by OrderComponent.activateTab() and by
   * the activeTab setter above.
   */
  ngOnChanges(): void {
    if (this.form && !this.allowStopOversRoute && !this.allowStopOversHourly) {
      this.removeAllStopOvers();
    }
  }

  ngOnInit() {
    this.setupAddressArray();
  }

  addStopOver() {
    // @ts-ignore
    this.form.controls['stopOvers'].controls.push(new UntypedFormControl([]));
    // @ts-ignore
    this.form.controls['stopOversModel'].controls.push(new UntypedFormControl([]));
    this.setupAddressArray();
  }

  /**
   * Removes every stopover row — both the form controls and the rendered rows. Called when the
   * active booking type has stopovers turned off.
   */
  removeAllStopOvers() {
    // @ts-ignore
    while (this.form.controls['stopOvers'].controls.length > 0) {
      this.removeDestination(0);
    }
    // The arrays can also be emptied from OrderComponent, which clears them for the settled
    // booking type before that tab reaches this component — that leaves the rendered rows
    // behind, so drop anything that is no longer the departure or the destination. A no-op
    // after the loop above, which already leaves exactly those two.
    this.addressArray = this.addressArray.filter(row =>
      row.control === this.form.controls['departure'] ||
      row.control === this.form.controls['destination']);
  }

  removeDestination(i: number) {
    // @ts-ignore
    this.form.controls['stopOvers'].controls.splice(i, 1);
    this.form.controls['stopOvers'].updateValueAndValidity();

    // @ts-ignore
    this.form.controls['stopOversModel'].controls.splice(i, 1);
    this.form.controls['stopOversModel'].updateValueAndValidity();
    this.addressArray.splice((i + 1), 1);
  }

  ngOnDestroy(): void {
  }

  toggleHelpModal(helpTitleTag: string, helpTextTag: string, type?: string, meta?: any) {
    this.openHelpModal.emit({
      helpTitleTag,
      helpTextTag,
      type,
      meta,
    });
  }

  checkWorkAreaEmit(property) {
    this.checkWorkArea.emit(property);
  }

  protected readonly faInfoCircle = 'info-circle';

  hasStopOvers() {
    // @ts-ignore
    return (this.addressArray && this.addressArray.length > 0)
  }

  setupAddressArray() {
    this.addressArray = [];
    this.addressArray.push({
      control:this.form.controls['departure'],
      controlModel:this.form.controls['departureModel'],
      config: this.departureConfig
    });
    // @ts-ignore
    if (this.form.controls['stopOvers'].controls.length > 0) {
      // @ts-ignore
      this.form.controls['stopOvers'].controls.forEach((control, i) => {
        this.stopOverConfig = JSON.parse(JSON.stringify(this.destinationConfig));
        this.stopOverConfig.formArray = 'stopOvers';
        this.stopOverConfig.label = 'stopover';
        this.stopOverConfig.property = i;

        this.addressArray.push({
          control:control,
          // @ts-ignore
          controlModel:this.form.controls['stopOversModel'].controls[i],
          config: this.stopOverConfig
        });
      });
    }

    // Only include the destination row when the active tab actually has a destination.
    // On hourly (without bookingHourlyDestination) there is no destination, but this
    // method runs on every (re)build — including ngOnInit after a next/back navigation,
    // where the activeTab setter's removal logic was skipped because `form` binds after
    // `activeTab` on construction. Without this guard the destination reappears on hourly
    // and the next switch to route turns it into a phantom stopover.
    const hourlyWithoutDestination = (!this.bookingHourlyDestination && this._activeTab && this._activeTab !== 'route');
    if (!hourlyWithoutDestination) {
      this.addressArray.push({
        control: this.form.controls['destination'],
        controlModel: this.form.controls['destinationModel'],
        config: this.destinationConfig
      });
    }
  }

  getPosY = (id: string) =>  {
    return (this.drag && id === this.drag.id ? this.yPos : 0);
  }

  getPosX = (id: string) =>  {
    return (this.drag && id === this.drag.id ? this.xPos : 0);
  }

  drop = (event: any, item: any) => {
    this.change = Math.round(this.yPos / this.rowHeight);
    if (this.change && this.change !== 0) {
      this.updatePrio(this.change, event);
      this.sort();
    }

    this.drag = null;
    this.yPos = 0;
    this.xPos = 0;
    document.querySelector('body').classList.remove('lock-screen');
  }

  /**
   * When mouse is pressed, define the origin from which to calculate
   * the change in direction for the element to be dragged
   */
  getEventCoordinates (e): any[] {
    let x,y;

    if (e.type == 'touchstart' || e.type == 'touchmove' || e.type == 'touchend' || e.type == 'touchcancel') {
      let touch = e.touches[0] || e.changedTouches[0];
      x = touch.clientX;
      y = touch.clientY;
    } else if (e.type == 'mousedown' || e.type == 'mouseup' || e.type == 'mousemove' || e.type == 'mouseover' || e.type == 'mouseout' || e.type == 'mouseenter' || e.type == 'mouseleave') {
      x = e.x;
      y = e.y;
    }
    return [x,y];
  }

  pick(event: any, item: any, index) {
    if(this.addressArray && this.addressArray.length <= 2) {
      return;
    }

    const coor = this.getEventCoordinates(event);
    this.yOrigin = coor[1];
    this.xOrigin = coor[0];
    this.drag = {
      id: item,
      index: index,
    };
  }
  /**
   * When the mouse moves, and while drag._id has a value, calculate
   * the change of position relative to the origin
   */
  move = (event: any, item: any) => {
    if(this.addressArray && this.addressArray.length <= 2) {
      return;
    }

    const coor = this.getEventCoordinates(event);
    if (this.drag) {
      this.yPos = coor[1] - this.yOrigin;
      this.xPos = coor[0] - this.xOrigin;
      if(!document.querySelector('body').classList.contains('lock-screen')) {
        document.querySelector('body').classList.add('lock-screen');
      }
    } else {
      this.yPos = 0;
      this.xPos = 0;
    }
    this.change = Math.round(this.yPos / (this.rowHeight + 30));
    if(this.change !== 0) {
      this.updatePrio(this.change, event);
      this.sort();
    }
  }

  sort() {
    /**
     * Set first element to departure
     */
    const dataArray = [];
    this.addressArray.forEach((control, i) => {
      dataArray.push(JSON.parse(JSON.stringify({
        control: control.control.value,
        controlModel: control.controlModel.value,
      })));
    });

    dataArray.forEach((data, i) => {
      delete data.index;
      if (i === 0) {
        this.form.controls['departure'].setValue(data.control);
        this.form.controls['departureModel'].setValue(data.controlModel);
        // this.departure = data;
      } else if (!this.addressArray[(i + 1)]) {
        // this.destination = data;
        this.form.controls['destination'].setValue(data.control);
        this.form.controls['destinationModel'].setValue(data.controlModel);
      } else
        // @ts-ignore
      if (this.form.controls['stopOvers'].controls.length > 0 && this.form.controls['stopOvers'].controls[(i - 1)]) {
        // @ts-ignore
        this.form.controls['stopOvers'].controls[(i - 1)].setValue(data.control);
        // @ts-ignore
        this.form.controls['stopOversModel'].controls[(i - 1)].setValue(data.controlModel);
      }
    });
    this.addressArray = [];
    this.setupAddressArray();
    this.form.controls['stopOvers'].updateValueAndValidity();
  }

  updatePrio(amount: number, event) {
    const coor = this.getEventCoordinates(event);

    if (!this.drag) {
      return;
    }
    // @ts-ignore
    const totalAddresses = this.form.controls['stopOvers'].controls.length + 2;
    if (amount) {
      if (amount > totalAddresses) {
        amount = totalAddresses;
      } else if (amount < (0 - totalAddresses)) {
        amount = (0 - totalAddresses);
      }
      const switchItem = this.drag.index + amount;
      if (this.addressArray[switchItem]) {
        const tmp =
          this.addressArray[switchItem];
        this.addressArray[switchItem] = this.addressArray[this.drag.index];
        this.addressArray[this.drag.index] = tmp;
        this.yPos = 0;
        this.xPos = 0;
        this.yOrigin = coor[1];
        this.xOrigin = coor[0];
        this.drag = null;
      }
      document.querySelector('body').classList.remove('lock-screen');
    }
  }

  getAddressArray() {
    return this.addressArray;
  }

  checkFieldValidity() {
    this.addressFields.forEach((address: AddressFieldComponent) => {
      address.checkFieldValidity();
    });
  }

  addErrors(error) {
    this.addressFields.forEach((address: AddressFieldComponent) => {
      if(address.config.property === error.property) {
        address.errors.addError(error);
      }
    });
  }

  removeErrors(error) {
    this.addressFields.forEach((address: AddressFieldComponent) => {
      if(address.config.property === error.property) {
        address.errors.removeError(error.id);
      }
    });
  }
}
